import {
  DatePickerProps,
  DateRange,
  DateRangePickerProps,
  DateRangeValidationError,
  DateValidationError,
  PickerChangeHandlerContext,
} from '@mui/x-date-pickers-pro';
import moment, { Moment } from 'moment/moment';

export type DateRange_Date = [Date | null, Date | null];

export const DATE_FORMAT = 'DD.MM.YYYY';

export const convertDateToMidnight = (date: string | Date): Date => {
  const convertedDate = new Date(date);
  convertedDate.setHours(0, 0, 0, 0);

  return convertedDate;
};

export const convertToDateOrNull = (newValue: unknown): Date | null => {
  if (newValue instanceof Date || newValue === null) {
    return newValue;
  }

  if (newValue instanceof moment) {
    return moment(newValue).toDate();
  }

  if (typeof newValue === 'string' || typeof newValue === 'number') {
    return moment(newValue).isValid() ? moment(newValue).toDate() : null;
  }

  return null;
};

export const convertToDateOrUndefined = (
  newValue: unknown,
): Date | undefined => {
  const dateOrNull = convertToDateOrNull(newValue);
  return !!dateOrNull ? dateOrNull : undefined;
};

export const isSameTime = (date1: Date, date2: Date): boolean =>
  moment(date1).isSame(date2, 'second');

// Date range
export const isDateWithinRange = (
  date: Date,
  rangeStart: Date,
  rangeEnd: Date,
): boolean => moment(date).isBetween(rangeStart, rangeEnd, 'day', '[]');

export const isDayInDates = (dates: Date[], day: Date) =>
  dates
    .map((d) => convertDateToMidnight(d).getTime())
    .includes(convertDateToMidnight(day).getTime());

export const isSameDay = (date1: Date, date2: Date) =>
  moment(date1).isSame(date2, 'day');

export const filterDatesByUniqueDays = (dates: Date[]) =>
  dates.filter((date, i, self) => {
    const time = convertDateToMidnight(date).getTime();

    return (
      self.findIndex((d) => convertDateToMidnight(d).getTime() === time) === i
    );
  });

export const getDateDifference = (rangeStart: Date, rangeEnd: Date): number =>
  moment(rangeEnd).diff(rangeStart);

export const getDaysInRange = (
  rangeStart: Date,
  rangeEnd: Date,
  includeStartDate?: boolean,
): number =>
  moment(rangeEnd)
    .add(includeStartDate ? 1 : 0, 'day')
    .diff(moment(rangeStart), 'days');

export const getDatesInRange = (rangeStart: Date, rangeEnd: Date): Date[] => {
  const totalDays = getDaysInRange(rangeStart, rangeEnd, true);

  return Array.from({ length: totalDays }, (_, i) =>
    moment(rangeStart).add(i, 'days').toDate(),
  );
};

export const getYearsOfDatesInRange = (
  rangeStart: Date,
  rangeEnd: Date,
): number[] => {
  const startYear = moment(rangeStart).year();
  const endYear = moment(rangeEnd).year();
  return Array.from(
    { length: endYear - startYear + 1 },
    (_, i) => startYear + i,
  );
};

export const sortDates = (dates: Date[]): Date[] =>
  dates.sort((a, b) => {
    return a.getTime() - b.getTime();
  });

// DateRange

export const getMomentRangeWithFullDays = (
  dateRange: DateRange<Moment>,
): DateRange<Moment> => {
  const [startDate, endDate] = dateRange;

  return [
    !!startDate ? moment(startDate).startOf('date') : null,
    !!endDate ? moment(endDate).endOf('date') : null,
  ];
};

export const getDateRangeWithFullDays = (
  dateRange: DateRange_Date,
): DateRange_Date => {
  const [startDate, endDate] = dateRange;

  return [
    !!startDate ? moment(startDate).startOf('date').toDate() : null,
    !!endDate ? moment(endDate).endOf('date').toDate() : null,
  ];
};

export const getFormattedDateString = (
  date: Date | undefined,
  format: string,
) => (!!date ? moment(date).format(format) : '');

export const convertToMomentOrNull = (
  value?: Date | string | null,
): Moment | null => (!!value ? moment(value) : null);

export const convertToMomentOrUndefined = (
  value?: Date | string | null | Moment,
): Moment | undefined => (!!value ? moment(value) : undefined);

type ConvertedDatePickerProps_Source = {
  value?: Date | null;
  onChange:
    | ((
        value: Date | null,
        context: PickerChangeHandlerContext<DateValidationError>,
      ) => void)
    | undefined;
  minDate?: Date;
  maxDate?: Date;
};
type ConvertedDatePickerProps_Picker = Pick<
  DatePickerProps,
  'value' | 'onChange' | 'minDate' | 'maxDate'
>;

type ConvertedDateRangePickerProps_Source = {
  value?: DateRange_Date;
  onChange:
    | ((
        value: DateRange_Date,
        context: PickerChangeHandlerContext<DateRangeValidationError>,
      ) => void)
    | undefined;
  minDate?: Date;
  maxDate?: Date;
};

type ConvertedDateRangePickerProps_Picker = Pick<
  DateRangePickerProps,
  'value' | 'onChange' | 'minDate' | 'maxDate'
>;

// Since MUI 6 update DatePickersProps date type must be Moment
export const convertDateTypeDatePickerProps = (
  props: ConvertedDatePickerProps_Source,
): ConvertedDatePickerProps_Picker => {
  const { value, onChange, minDate, maxDate } = props;

  return {
    value: convertToMomentOrNull(value),
    onChange: !!onChange
      ? (value, context) =>
          onChange((!!value && value.toDate()) || null, context)
      : undefined,
    minDate: convertToMomentOrUndefined(minDate),
    maxDate: convertToMomentOrUndefined(maxDate),
  };
};

export const convertDateTypeDateRangePickerProps = (
  props: ConvertedDateRangePickerProps_Source,
): ConvertedDateRangePickerProps_Picker => {
  const { value, onChange, minDate, maxDate } = props;

  return {
    value: [
      convertToMomentOrNull(value?.[0]),
      convertToMomentOrNull(value?.[1]),
    ],
    onChange: !!onChange
      ? (value, context) => {
          onChange(
            [convertToDateOrNull(value[0]), convertToDateOrNull(value[1])],
            context,
          );
        }
      : undefined,
    minDate: convertToMomentOrUndefined(minDate),
    maxDate: convertToMomentOrUndefined(maxDate),
  };
};
